/** 本機 mock 控制面：不打網路，給 IS_MOCK／空 API_BASE 用。 */
import { countAliveNodes, fleetNodeFixture, seedFleetNodes } from './fleetNodes';
import { OUTCOME_EVENTS } from './outcomeEvents';
import { readRoomMeta, type RoomMeta } from './roomMeta';

export type FleetMessage = {
  id: string;
  sessionId: string;
  platform: string;
  direction: string;
  actor: string;
  body: string;
  ts: string;
  pendingSend?: boolean;
  meta?: RoomMeta;
};

export type FleetSession = {
  sessionId: string;
  platform: string;
  peerKey?: string | null;
  mode: 'auto' | 'human';
  assignee?: string | null;
  title?: string | null;
  lastActivity: string;
  endedAt?: string | null;
};

type FleetGrant = {

  id: string;
  operatorEmail: string;
  platform: string;
  persona: string;
  canTakeover: number;
  canSend: number;
};

type StoredMessage = Omit<FleetMessage, 'meta'> & { meta?: Record<string, unknown> };

type FleetOutcome = {
  sessionId: string;
  event: string;
  occurredAt: string;
};

type FleetMemory = {
  version: number;
  params: Record<string, unknown>;
  updatedAt: string;
  updatedBy: string;
  sessions: FleetSession[];
  messages: StoredMessage[];
  outcomes: FleetOutcome[];
  grants: FleetGrant[];
};

const MOCK_STAMP = '2026-11-03T00:00:00.000Z';
const MOCK_PARAMS_KEY = 'chat-rea.mock-fleet-params';

const SEED_PARAMS: Record<string, unknown> = {
  demo_flag: false,
  scan_enabled: { wootalk: true, ut: true, ek21: true, jd: true, site: true },
  link_enabled: { wootalk: true, ut: true, ek21: true, jd: true, site: true },
  scanRooms: true,
  sendLink: false,
};

function seedFleetMemory(): FleetMemory {
  const stored = readStoredFleetParams();
  return {
    version: stored?.version ?? 1,
    params: stored?.params ?? SEED_PARAMS,
    updatedAt: stored?.updatedAt ?? MOCK_STAMP,
    updatedBy: stored?.updatedBy ?? 'mock',
    sessions: [
      {
        sessionId: 'mock-wootalk-1',
        platform: 'wootalk',
        peerKey: 'peer-night',
        mode: 'auto',
        assignee: null,
        title: '夜聊小安',
        lastActivity: '2026-11-03T01:00:00.000Z',
      },
      {
        sessionId: 'mock-wootalk-2',
        platform: 'wootalk',
        peerKey: 'peer-noon',
        mode: 'human',
        assignee: 'operator@example.com',
        title: '午安路人',
        lastActivity: '2026-11-03T03:00:00.000Z',
      },
      {
        sessionId: 'mock-wootalk-3',
        platform: 'wootalk',
        peerKey: 'peer-late',
        mode: 'auto',
        assignee: null,
        title: '晚到的人',
        lastActivity: '2026-11-03T00:30:00.000Z',
        endedAt: '2026-11-03T00:45:00.000Z',
      },
      {
        sessionId: 'mock-ut-1',
        platform: 'ut',
        peerKey: '路人甲',
        mode: 'auto',
        assignee: null,
        title: '公開房',
        lastActivity: '2026-11-03T02:00:00.000Z',
      },
    ],
    messages: [
      {
        id: 'mock-msg-1',
        sessionId: 'mock-wootalk-1',
        platform: 'wootalk',
        direction: 'in',
        actor: 'peer',
        body: '大家好',
        ts: MOCK_STAMP,
        pendingSend: false,
        meta: { roomId: '11124#3', roomName: '成人聊天室' },
      },
      {
        id: 'mock-msg-2',
        sessionId: 'mock-wootalk-1',
        platform: 'wootalk',
        direction: 'out',
        actor: 'bot',
        body: '有人在嗎',
        ts: '2026-11-03T00:00:30.000Z',
        pendingSend: false,
      },
      {
        id: 'mock-msg-3',
        sessionId: 'mock-wootalk-2',
        platform: 'wootalk',
        direction: 'out',
        actor: 'operator',
        body: '等我回你',
        ts: '2026-11-03T00:01:00.000Z',
        pendingSend: true,
      },
      {
        id: 'mock-msg-4',
        sessionId: 'mock-wootalk-1',
        platform: 'wootalk',
        direction: 'in',
        actor: 'peer',
        body: '還在嗎',
        ts: '2026-11-03T01:00:00.000Z',
        pendingSend: false,
      },
      {
        id: 'mock-msg-5',
        sessionId: 'mock-wootalk-3',
        platform: 'wootalk',
        direction: 'in',
        actor: 'peer',
        body: '晚安',
        ts: '2026-11-03T00:30:00.000Z',
        pendingSend: false,
      },
      {
        id: 'mock-msg-6',
        sessionId: 'mock-ut-1',
        platform: 'ut',
        direction: 'out',
        actor: 'bot',
        body: '有人在嗎',
        ts: '2026-11-03T02:00:00.000Z',
        pendingSend: false,
        meta: { roomId: '11124#3', account: 'secret-account' },
      },
    ],
    outcomes: [{ sessionId: 'mock-wootalk-1', event: 'link_sent', occurredAt: MOCK_STAMP }],
    grants: [
      {
        id: 'mock-grant-1',
        operatorEmail: 'operator@example.com',
        platform: '*',
        persona: 'default',
        canTakeover: 1,
        canSend: 1,
      },
    ],
  };
}

let fleetMemory: FleetMemory | null = null;
let nextMessage = 7;
let nextGrant = 2;

type FleetMockListener = (event: unknown) => void;
const fleetMockListeners = new Set<FleetMockListener>();

export function subscribeFleetMock(listener: FleetMockListener): () => void {
  fleetMockListeners.add(listener);
  return () => {
    fleetMockListeners.delete(listener);
  };
}

function emitFleetMock(event: unknown): void {
  for (const listener of fleetMockListeners) listener(event);
}

function memory(): FleetMemory {
  fleetMemory ??= seedFleetMemory();
  return fleetMemory;
}

export function resetFleetMock(): void {
  fleetMemory = null;
  nextMessage = 7;
  nextGrant = 2;
  clearStoredFleetParams();
}

function sessionStore(): Storage | null {
  try {
    if (typeof sessionStorage === 'undefined') return null;
    return sessionStorage;
  } catch {
    return null;
  }
}

function readStoredFleetParams(): {
  version: number;
  params: Record<string, unknown>;
  updatedAt: string;
  updatedBy: string;
} | null {
  const storage = sessionStore();
  if (!storage) return null;
  const raw = storage.getItem(MOCK_PARAMS_KEY);
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null;
    const row = parsed as Record<string, unknown>;
    if (typeof row.version !== 'number' || !Number.isFinite(row.version)) return null;
    if (typeof row.params !== 'object' || row.params === null || Array.isArray(row.params)) return null;
    return {
      version: row.version,
      params: row.params as Record<string, unknown>,
      updatedAt: typeof row.updatedAt === 'string' ? row.updatedAt : MOCK_STAMP,
      updatedBy: typeof row.updatedBy === 'string' ? row.updatedBy : 'mock',
    };
  } catch {
    return null;
  }
}

function writeStoredFleetParams(row: {
  version: number;
  params: Record<string, unknown>;
  updatedAt: string;
  updatedBy: string;
}): void {
  sessionStore()?.setItem(MOCK_PARAMS_KEY, JSON.stringify(row));
}

function clearStoredFleetParams(): void {
  sessionStore()?.removeItem(MOCK_PARAMS_KEY);
}

function fleetNodesQuery(url: URL): string {
  const fromPath = url.searchParams.get('fixture') ?? '';
  if (fromPath !== '') return fromPath;
  if (typeof window === 'undefined') return '';
  return new URLSearchParams(window.location.search).get('fleetNodes') ?? '';
}

function readJsonBody(init?: RequestInit): Record<string, unknown> {
  if (typeof init?.body !== 'string' || init.body === '') return {};
  const parsed: unknown = JSON.parse(init.body);
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    throw new Error('body 必須是物件');
  }
  return parsed as Record<string, unknown>;
}

export function mockFleetRequest<T>(path: string, init?: RequestInit): T {
  const method = (init?.method ?? 'GET').toUpperCase();
  const url = new URL(path, 'http://fleet.local');
  const pathname = url.pathname;
  const store = memory();

  if (method === 'GET' && pathname === '/api/fleet/sessions') {
    const mode = url.searchParams.get('mode');
    const platform = url.searchParams.get('platform');
    const life = url.searchParams.get('life');
    const sessions = store.sessions.filter((session) => {
      if (mode !== null && session.mode !== mode) return false;
      if (platform !== null && session.platform !== platform) return false;
      const ended = (session.endedAt?.trim() ?? '') !== '';
      if (life === 'alive' && ended) return false;
      if (life === 'ended' && !ended) return false;
      return true;
    });
    return { sessions } as T;
  }

  if (method === 'POST' && pathname === '/api/fleet/messages') {
    const body = readJsonBody(init);
    const sessionId = typeof body.sessionId === 'string' ? body.sessionId.trim() : '';
    const text = typeof body.body === 'string' ? body.body : '';
    const direction = body.direction === 'in' || body.direction === 'out' ? body.direction : '';
    if (sessionId === '' || text === '' || direction === '') throw new Error('需要 sessionId, direction, body');
    const session = store.sessions.find((row) => row.sessionId === sessionId);
    if (!session) throw new Error(`找不到 session ${sessionId}`);
    const explicit = typeof body.id === 'string' ? body.id.trim() : '';
    const id = explicit || `mock-msg-${nextMessage}`;
    if (explicit === '') nextMessage += 1;
    const ts = typeof body.ts === 'string' && body.ts !== '' ? body.ts : new Date().toISOString();
    const message: FleetMessage = {
      id,
      sessionId,
      platform: typeof body.platform === 'string' && body.platform !== '' ? body.platform : session.platform,
      direction,
      actor: typeof body.actor === 'string' && body.actor !== '' ? body.actor : direction === 'in' ? 'peer' : 'human',
      body: text,
      ts,
      pendingSend: body.pendingSend === true,
      meta: readRoomMeta(body.meta),
    };
    store.messages.push(message);
    session.lastActivity = ts;
    emitFleetMock({ type: 'message', ...message });
    return { ok: true, id } as T;
  }

  if (method === 'GET' && pathname === '/api/fleet/messages') {
    const sessionId = url.searchParams.get('sessionId');
    const since = url.searchParams.get('since');
    const limitRaw = Number(url.searchParams.get('limit') ?? '');
    let messages = store.messages.filter((message) => {
      if (sessionId !== null && message.sessionId !== sessionId) return false;
      if (since !== null && message.ts <= since) return false;
      return true;
    });
    if (Number.isFinite(limitRaw) && limitRaw > 0) messages = messages.slice(-limitRaw);
    const visible = messages.map((message) => ({ ...message, meta: readRoomMeta(message.meta) }));
    if (sessionId === null) return { messages: visible } as T;
    const outcomes = store.outcomes
      .filter((row) => row.sessionId === sessionId && (OUTCOME_EVENTS as readonly string[]).includes(row.event))
      .map((row) => ({ event: row.event, occurredAt: row.occurredAt }));
    return { messages: visible, outcomes } as T;
  }

  const modeMatch = /^\/api\/fleet\/sessions\/([^/]+)\/mode$/.exec(pathname);
  if (method === 'PATCH' && modeMatch) {
    const sessionId = decodeURIComponent(modeMatch[1] ?? '');
    const body = readJsonBody(init);
    if (body.mode !== 'auto' && body.mode !== 'human') throw new Error('mode 只能是 auto 或 human');
    const session = store.sessions.find((row) => row.sessionId === sessionId);
    if (!session) throw new Error(`找不到 session ${sessionId}`);
    session.mode = body.mode;
    return { ok: true, mode: session.mode } as T;
  }

  const sayMatch = /^\/api\/fleet\/sessions\/([^/]+)\/say$/.exec(pathname);
  if (method === 'POST' && sayMatch) {
    const sessionId = decodeURIComponent(sayMatch[1] ?? '');
    const body = readJsonBody(init);
    const text = typeof body.text === 'string' ? body.text : '';
    const session = store.sessions.find((row) => row.sessionId === sessionId);
    if (!session) throw new Error(`找不到 session ${sessionId}`);
    const id = `mock-msg-${nextMessage}`;
    nextMessage += 1;
    const ts = new Date().toISOString();
    store.messages.push({
      id,
      sessionId,
      platform: session.platform,
      direction: 'out',
      actor: 'human',
      body: text,
      ts,
      pendingSend: true,
    });
    session.lastActivity = ts;
    return { ok: true, id } as T;
  }

  const ackMatch = /^\/api\/fleet\/sessions\/([^/]+)\/outbound\/([^/]+)\/ack$/.exec(pathname);
  if (method === 'POST' && ackMatch) {
    const sessionId = decodeURIComponent(ackMatch[1] ?? '');
    const msgId = decodeURIComponent(ackMatch[2] ?? '');
    const message = store.messages.find((row) => row.id === msgId && row.sessionId === sessionId);
    if (!message) throw new Error(`找不到訊息 ${msgId}`);
    message.pendingSend = false;
    return { ok: true } as T;
  }

  if (method === 'GET' && pathname === '/api/fleet/config') {
    return {
      version: store.version,
      params: store.params,
      updatedAt: store.updatedAt,
      updatedBy: store.updatedBy,
    } as T;
  }

  if (method === 'PUT' && pathname === '/api/fleet/config') {
    const body = readJsonBody(init);
    if (typeof body.params !== 'object' || body.params === null || Array.isArray(body.params)) {
      throw new Error('params 必須是物件');
    }
    store.params = body.params as Record<string, unknown>;
    store.version += 1;
    store.updatedAt = new Date().toISOString();
    store.updatedBy = 'mock';
    writeStoredFleetParams({
      version: store.version,
      params: store.params,
      updatedAt: store.updatedAt,
      updatedBy: store.updatedBy,
    });
    return { ok: true, version: store.version } as T;
  }

  if (method === 'GET' && pathname === '/api/fleet/monitor') {
    const hours = Number(url.searchParams.get('hours') ?? '24');
    const counts = new Map<string, number>();
    for (const session of store.sessions) counts.set(session.mode, (counts.get(session.mode) ?? 0) + 1);
    return {
      hours: Number.isFinite(hours) ? hours : 24,
      outcomes: [
        { event: 'link_sent', n: 10 },
        { event: 'click', n: 3 },
        { event: 'signup', n: 2 },
      ],
      byPlatform: [
        { platform: 'wootalk', link_sent: 4, click: 2, signup: 1 },
        { platform: 'ut', link_sent: 3, click: 0, signup: 0 },
        { platform: 'ek21', link_sent: 2, click: 1, signup: 0 },
        { platform: 'site', link_sent: 1, click: 0, signup: 1 },
        { platform: 'jd', link_sent: 0, click: 0, signup: 0 },
      ],
      modes: [...counts.entries()].map(([mode, n]) => ({ mode, n })),
      humanOpen: store.sessions.filter((session) => session.mode === 'human').length,
      messages: store.messages.length,
      paramsVersion: store.version,
      params: store.params,
      nodesAlive: fleetNodesQuery(url) === 'empty' ? 0 : countAliveNodes(Date.now()),
    } as T;
  }

  if (method === 'GET' && pathname === '/api/fleet/nodes') {
    const fixture = fleetNodesQuery(url);
    if (fixture === 'empty') return { nodes: [] } as T;
    if (fixture !== '') return { nodes: fleetNodeFixture(fixture, Date.now()) } as T;
    return { nodes: seedFleetNodes(Date.now()) } as T;
  }

  if (method === 'GET' && pathname === '/api/fleet/grants') {
    return { grants: [...store.grants] } as T;
  }

  if (method === 'POST' && pathname === '/api/fleet/grants') {
    const body = readJsonBody(init);
    const email = typeof body.operatorEmail === 'string' ? body.operatorEmail : '';
    if (email.trim() === '') throw new Error('operatorEmail 必填');
    const id = `mock-grant-${nextGrant}`;
    nextGrant += 1;
    store.grants.push({
      id,
      operatorEmail: email,
      platform: typeof body.platform === 'string' && body.platform !== '' ? body.platform : '*',
      persona: typeof body.persona === 'string' && body.persona !== '' ? body.persona : 'default',
      canTakeover: body.canTakeover === false ? 0 : 1,
      canSend: body.canSend === false ? 0 : 1,
    });
    return { ok: true, id } as T;
  }

  if (method === 'DELETE' && pathname === '/api/fleet/grants') {
    const id = url.searchParams.get('id');
    const before = store.grants.length;
    store.grants = store.grants.filter((grant) => grant.id !== id);
    if (store.grants.length === before) throw new Error(`找不到授權 ${id ?? ''}`);
    return { ok: true } as T;
  }

  throw new Error(`mock fleet 沒有這條路徑：${method} ${path}`);
}
